/**
 * AuthLayout — premium split-panel layout for auth pages (login, signup, forgot-password).
 *
 * Left panel:  branded Waypoint showcase (hidden on mobile, visible md+)
 * Right panel: the form content (full width on mobile, centered on desktop)
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { WaypointMark } from '../brand/WaypointMark';

/* ─── Waypoint Logo (matches MarketingNav / existing Brand component) ─── */
function WaypointLogo({ size = 'lg' }: { size?: 'sm' | 'lg' }) {
  return (
    <Link to="/" className="flex items-center gap-3">
      <WaypointMark size={size === 'lg' ? 48 : 36} rounded="rounded-lg" />
      <div className="flex flex-col leading-none">
        <span className="font-mono text-sm tracking-widest text-ink">WAYPOINT</span>
        <span className="mt-1 font-mono text-2xs tracking-hyper text-ink-muted">
          INTELLIGENCE&nbsp;PLATFORM
        </span>
      </div>
    </Link>
  );
}

/* ─── Value proposition bullets ─── */
const VALUE_PROPS = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <circle cx="12" cy="12" r="8" opacity="0.4" />
        <line x1="12" y1="1" x2="12" y2="4" />
        <line x1="12" y1="20" x2="12" y2="23" />
        <line x1="1" y1="12" x2="4" y2="12" />
        <line x1="20" y1="12" x2="23" y2="12" />
      </svg>
    ),
    text: 'Predictive models for churn, deals and when invoices get paid',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    text: 'Every meeting, deal, and client — connected',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    text: 'Predictions that actually predict',
  },
];

/* ─── Layout ─── */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen">
      {/* ── Left branded panel (hidden on mobile) ── */}
      <div className="relative hidden w-[480px] shrink-0 flex-col justify-between overflow-hidden border-r border-border bg-surface-0 p-10 md:flex lg:w-[520px]">
        {/* Ambient glow effects */}
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full opacity-30"
            style={{
              background: 'radial-gradient(circle, rgba(155, 58, 248,0.15) 0%, transparent 70%)',
            }}
          />
          <div
            className="absolute -bottom-40 -right-40 h-[600px] w-[600px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle, rgba(255, 74, 226,0.12) 0%, transparent 70%)',
            }}
          />
        </div>

        {/* Subtle grid overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        {/* Content */}
        <div className="relative z-raised">
          <WaypointLogo size="lg" />

          <div className="mt-16">
            <h2 className="font-serif text-3xl font-medium leading-tight text-ink lg:text-4xl lg:leading-(--text-4xl--line-height)">
              Intelligence that
              <br />
              <span className="bg-linear-to-r from-primary-bright to-primary bg-clip-text text-transparent">
                drives growth
              </span>
            </h2>

            <div className="mt-10 space-y-5">
              {VALUE_PROPS.map((prop) => (
                <div key={prop.text} className="flex items-start gap-3.5">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-1/60 text-primary-bright">
                    {prop.icon}
                  </div>
                  <span className="pt-1.5 text-sm leading-snug text-ink-muted">{prop.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom decorative gradient bar */}
        <div className="relative z-raised">
          <div className="h-px w-full bg-linear-to-r from-transparent via-primary-line to-transparent" />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-ink-dim">
            Trusted by growing firms everywhere
          </p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        {/* Mobile-only logo (hidden md+) */}
        <div className="mb-10 md:hidden">
          <WaypointLogo size="sm" />
        </div>

        <div className="w-full max-w-md">
          {children}
        </div>

        <p className="mt-6 text-center text-xs text-ink-dim">
          <Link to="/" className="hover:text-ink-muted">&larr; Back to home</Link>
        </p>
      </div>
    </div>
  );
}
